import { breadcrumbSlot, useBreadcrumb } from "@redotech/arbiter/breadcrumb";
import {
  RedoHorizontalTab,
  RedoHorizontalTabs,
} from "@redotech/arbiter/tabs/redo-horizontal-tabs";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { TabsPortal } from "@redotech/redo-web/tabs-portal";
import { memo } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

export const WAREHOUSE_METRICS_TABS = [
  "inbound-dashboard",
  "processing-pipeline",
  "qa-metrics",
  "refulfillment",
  "warehouse-slas",
] as const;

export type WarehouseMetricsTab = (typeof WAREHOUSE_METRICS_TABS)[number];

export const warehouseMetricsBreadcrumb = breadcrumbSlot();

const warehouseMetricsTabSet: ReadonlySet<string> = new Set(
  WAREHOUSE_METRICS_TABS,
);

function isWarehouseMetricsTab(
  value: string | undefined,
): value is WarehouseMetricsTab {
  return value !== undefined && warehouseMetricsTabSet.has(value);
}

function warehouseMetricsRoot(pathname: string): string | undefined {
  return pathname.match(/^(.*\/warehouse-metrics)(?:\/[^/]+)?\/?$/)?.[1];
}

export const WarehouseMetricsLayout = memo(function WarehouseMetricsLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useBreadcrumb(
    warehouseMetricsBreadcrumb,
    t("Warehouse metrics", {
      context:
        "warehouse page title for inbound, processing, quality, refulfillment, and service-level metrics",
    }),
  );

  const pathTab = pathname.match(/\/warehouse-metrics\/([^/]+)\/?$/)?.[1];
  const selectedTab = isWarehouseMetricsTab(pathTab)
    ? pathTab
    : "inbound-dashboard";

  const setSelectedTab = useHandler((tab: WarehouseMetricsTab) => {
    const root = warehouseMetricsRoot(pathname);
    if (!root) {
      return;
    }
    navigate(`${root}/${tab}`);
  });

  const tabs: RedoHorizontalTab<WarehouseMetricsTab>[] = [
    {
      key: "inbound-dashboard",
      label: t("Inbound", {
        context: "warehouse metrics tab for returns arriving at the warehouse",
      }),
    },
    {
      key: "processing-pipeline",
      label: t("Processing", {
        context:
          "warehouse metrics tab for the order and warehouse work processing pipeline",
      }),
    },
    {
      key: "qa-metrics",
      label: t("QA", {
        context: "warehouse metrics tab for quality assurance metrics",
      }),
    },
    {
      key: "refulfillment",
      label: t("Fulfillment", {
        context: "warehouse metrics tab for same-day order fulfillment",
      }),
    },
    {
      key: "warehouse-slas",
      label: t("SLAs", {
        context:
          "warehouse metrics tab for service-level agreement performance",
      }),
    },
  ];

  return (
    <>
      <TabsPortal>
        <RedoHorizontalTabs
          pl="none"
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          tabs={tabs}
        />
      </TabsPortal>
      <Outlet />
    </>
  );
});

export const WarehouseMetricsLegacyRedirect = memo(
  function WarehouseMetricsLegacyRedirect({
    tab,
  }: {
    tab: WarehouseMetricsTab;
  }) {
    const { pathname, search } = useLocation();
    const base = pathname.replace(/\/[^/]+\/?$/, "");
    return (
      <Navigate
        replace
        to={{ pathname: `${base}/warehouse-metrics/${tab}`, search }}
      />
    );
  },
);
